// assets
import BookOutlined from '@ant-design/icons/BookOutlined';
import PictureOutlined from '@ant-design/icons/PictureOutlined';
import TeamOutlined from '@ant-design/icons/TeamOutlined';

// ==============================|| SHAREABLE CLINICAL MENU ITEMS ||============================== //

/**
 * Clinical sidebar pages whose screen is a SHAREABLE component (a thin wrapper over one
 * shared implementation), so it works correctly for any org role. These can be granted to
 * ANY persona-tree role via Role Definitions and are injected into that role's menu at the
 * role's own URL (`/<persona>/<subpath>`), shown only when the role grants the code.
 *
 * This is the single source of "clinical pages any role may be granted": adding a new
 * shareable page here makes it grantable to every role at once. Role-SPECIFIC screens
 * (Calendar, Dashboard, Reports, Settings, Data Collection, Child list) are deliberately
 * NOT here — they are large, role-scoped components and stay in each role's own tree.
 *
 * `code` is the Features and Pages page code (rbac_app_page.code) used both as the grant
 * key (granted_codes) and the catalogueCode the strict grant filter matches on. Routes for
 * these subpaths are mounted per role in routes/MainRoutes.jsx, reusing the shared
 * components (e.g. /therapist/materials → SupervisorMaterials → MaterialRepositoryView).
 */
/**
 * Personas whose ROUTE PREFIX differs from their role code. The injected URL is built from
 * the prefix, not the code: the Caregiver role is `parent` but its screens live under
 * /caregiver, and building `/parent/materials` produced a sidebar item pointing at a route
 * that does not exist.
 */
const PERSONA_ROUTE_PREFIX = { parent: 'caregiver' };

/**
 * Personas that may only be offered SOME of the shareables, by page code. A persona absent
 * from here is offered all of them, which is the existing behaviour.
 *
 * The caregiver is limited to the two REPOSITORY pages. Therapist Profiles is staff
 * directory data with no guardian scoping story — a parent has a case for seeing their own
 * child's therapists, none for the centre's whole roster — so it is not offered until that
 * scoping exists, rather than left to whether somebody happens to tick it.
 */
const PERSONA_SHAREABLES = { parent: ['skill_repository_page', 'material_repository_page'] };

export const SHAREABLE_CLINICAL = [
  { code: 'therapist_directory', title: 'Therapists', icon: TeamOutlined, subpath: 'therapists' },
  { code: 'skill_repository_page', title: 'Skill Repository', icon: BookOutlined, subpath: 'skills' },
  { code: 'material_repository_page', title: 'Material Repository', icon: PictureOutlined, subpath: 'materials' }
];

/**
 * Return a copy of `tree` with the shareable items the persona does not already carry
 * appended (deduped by catalogueCode), each pointing at the persona's own URL. A persona
 * that already lists an item (e.g. Supervisor has Materials) keeps its own entry untouched.
 */
export function injectShareableClinical(tree, persona) {
  const prefix = PERSONA_ROUTE_PREFIX[persona] || persona;
  const allowed = PERSONA_SHAREABLES[persona];
  const have = new Set((tree?.children || []).map((c) => c.catalogueCode).filter(Boolean));
  const extras = SHAREABLE_CLINICAL.filter((s) => !have.has(s.code) && (!allowed || allowed.includes(s.code))).map((s) => ({
    id: `shareable-${persona}-${s.code}`,
    title: s.title,
    type: 'item',
    url: `/${prefix}/${s.subpath}`,
    icon: s.icon,
    breadcrumbs: false,
    catalogueCode: s.code
  }));
  return extras.length ? { ...tree, children: [...(tree.children || []), ...extras] } : tree;
}

export default SHAREABLE_CLINICAL;
